import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { INTERPRETER_FAQ_CATEGORIES } from "@/content/interpreter-corpus/faq-defs";

export const Route = createFileRoute("/interpreter/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/interpreter-corpus");
    return { faqs: m.INTERPRETER_FAQ_TH };
  },
  component: InterpreterFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ล่ามราชการและล่ามศาลสำหรับชาวต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องล่ามในศาล ล่ามชั้นสอบสวนที่สถานีตำรวจ ล่ามที่อำเภอ/สำนักทะเบียน ล่ามที่สำนักงานตรวจคนเข้าเมือง ล่ามที่สำนักงานที่ดิน ล่ามกับทนายความผู้ทำคำรับรองลายมือชื่อ ล่ามที่สถานทูตและกรมการกงสุล ล่ามการแพทย์ ล่ามธุรกิจ พร้อมคุณสมบัติและจรรยาบรรณของล่าม",
        path: "/interpreter/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/interpreter/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ล่ามราชการและล่ามศาล", url: `${site}/interpreter/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/interpreter/faq")) },
      ],
    };
  },
});

function InterpreterFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ล่ามราชการและล่ามศาล</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ล่ามราชการ ล่ามศาล และล่ามชั้นสอบสวน — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          คำตอบอ้างอิงประมวลกฎหมายวิธีพิจารณาความอาญา มาตรา 13 (การจัดหาล่ามในชั้นสอบสวนและชั้นศาล) ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 (ภาษาที่ใช้ในศาลและคำแปลเอกสาร) แนวปฏิบัติของสำนักงานศาลยุติธรรม (coj.go.th) สำนักบริหารการทะเบียน กรมการปกครอง (bora.dopa.go.th) สำนักงานตรวจคนเข้าเมือง (immigration.go.th) กรมที่ดิน (dol.go.th) สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th) กรมการกงสุล (consular.mfa.go.th) และพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 โดยอนุสัญญา Apostille จะมีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 ข้อมูลนี้เป็นข้อมูลทั่วไป โปรดตรวจสอบกับหน่วยงานปลายทางก่อนดำเนินการ
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/interpreter/faq" className="text-primary underline">Court &amp; government interpreter FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {INTERPRETER_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/interpreter/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ขอจัดหาล่าม</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งภาษา วันเวลา สถานที่ และประเภทงาน (ศาล ตำรวจ อำเภอ ที่ดิน ตม. โรงพยาบาล หรือธุรกิจ) ได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00 เจ้าหน้าที่จะยืนยันคิวล่ามและแจ้งค่าบริการก่อนเริ่มงาน
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="interpreter" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}